import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { useI18n } from '../i18n/useI18n';
import type { Tone } from './StatusBadge';

/**
 * `subtle` is the one a table row carries: quieter than `ghost`, because a
 * column of them down a list must not read as a column of things to press.
 * Its style has been in the sheet since the reference build; what was missing
 * was a way to ask for it.
 */
type ButtonVariant = 'primary' | 'ghost' | 'danger' | 'subtle';

type ButtonProps = {
  variant?: ButtonVariant;
  size?: 'sm' | 'md';
  block?: boolean;
} & ButtonHTMLAttributes<HTMLButtonElement>;

export function Button({ variant = 'ghost', size = 'md', block = false, className, type = 'button', ...rest }: ButtonProps) {
  const classes = [
    'btn',
    `btn-${variant}`,
    size === 'sm' ? 'btn-sm' : '',
    block ? 'btn-block' : '',
    className ?? '',
  ]
    .filter(Boolean)
    .join(' ');
  return <button type={type} className={classes} {...rest} />;
}

export function Card({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={['card', className ?? ''].filter(Boolean).join(' ')}>{children}</div>;
}

export function Chip({ children }: { children: ReactNode }) {
  return <span className="chip">{children}</span>;
}

export function Labels({ labels, empty }: { labels: Record<string, string>; empty: string }) {
  const entries = Object.entries(labels);
  if (entries.length === 0) return <span className="faint">{empty}</span>;
  return (
    <span className="label-list">
      {entries.map(([key, value]) => (
        <Chip key={key}>
          {key}
          {'='}
          {value}
        </Chip>
      ))}
    </span>
  );
}

/**
 * The head of a page: what it is, what it is about, and what may be done here.
 *
 * **There is no `description`.** There was one, and six of the seven screens
 * filled it with a paragraph that is read once and scrolled past every working
 * day afterwards. A parameter for prose is a parameter that gets prose in it,
 * so the way to keep it off the first screen is to leave nowhere to put it.
 * What a screen is for is said by what it shows.
 */
export function PageHead({
  title,
  sub,
  actions,
}: {
  title: string;
  /**
   * What the title names, in the answer's own words: a domain, a slug.
   *
   * A value and never a sentence — it comes from the server on every use, and
   * a page headed with a customer's name has to say which customer that is on
   * the wire.
   */
  sub?: string;
  actions?: ReactNode;
}) {
  return (
    <div className="page-head">
      <div>
        {/* Focus target after navigation, so a keyboard lands at the top of the new page. */}
        <h1 className="pt" tabIndex={-1} id="page-title">
          {title}
        </h1>
        {sub !== undefined && <p className="ps mono">{sub}</p>}
      </div>
      {actions !== undefined && <div className="ph-actions">{actions}</div>}
    </div>
  );
}

export function EmptyState({
  title,
  body,
  action,
}: {
  title: string;
  body?: string;
  /** The one thing to do from here, where there is one. */
  action?: ReactNode;
}) {
  return (
    <div className="empty">
      <p className="etitle">{title}</p>
      {body !== undefined && <p>{body}</p>}
      {action !== undefined && <div className="empty-action">{action}</div>}
    </div>
  );
}

export function Loading() {
  const { t } = useI18n();
  return (
    <div className="empty" role="status">
      <p>
        {t('common.loading')}
        {'…'}
      </p>
    </div>
  );
}

/**
 * A figure that could not be counted: what kind of hole it is, and the server's
 * reason for it.
 *
 * The wording comes from the caller because the two callers mean different
 * things by it — a figure the control plane could not read this time, and a
 * component nothing can read at all — and a tile that chose one word for both
 * would be the tile telling the reader they are the same thing.
 */
export interface StatAbsent {
  label: string;
  reason?: string;
}

type StatProps = {
  label: string;
  sub?: ReactNode;
  /**
   * Colours the figure. For a count of things that are wrong, given only when
   * the count is above zero: a red nought is a fault where there is none.
   */
  tone?: Tone;
  /** Makes the tile a control, for a count whose members are worth opening. */
  onClick?: () => void;
} & ({ value: ReactNode; absent?: undefined } | { value?: undefined; absent: StatAbsent });

/**
 * One figure, in a box.
 *
 * `value` is a node rather than a string so that a state can stand where a
 * number would — a tenancy's state and a certificate's outcome are the answer
 * to "how is it", and spelling them out as words in a tile beats a number
 * nobody asked for. `absent` is the other half of the same rule: an answer that
 * is missing is drawn as missing, with the reason, and never as a nought.
 */
export function Stat({ label, value, sub, tone, absent, onClick }: StatProps) {
  const classes = [
    'card',
    'stat',
    absent === undefined ? '' : 'stat-absent',
    onClick === undefined ? '' : 'stat-action',
  ]
    .filter(Boolean)
    .join(' ');

  const tint = tone === undefined ? {} : { 'data-tone': tone };

  const body = (
    <>
      <div className="sl">{label}</div>
      {absent === undefined ? (
        <div className="sv">{value}</div>
      ) : (
        <>
          <div className="sv-absent">{absent.label}</div>
          {absent.reason !== undefined && <div className="sd mono">{absent.reason}</div>}
        </>
      )}
      {sub !== undefined && <div className="sd">{sub}</div>}
    </>
  );

  if (onClick !== undefined) {
    return (
      <button type="button" className={classes} onClick={onClick} {...tint}>
        {body}
      </button>
    );
  }
  return (
    <div className={classes} {...tint}>
      {body}
    </div>
  );
}
